import { BasicPage } from "../components/BasicPage";
import { useAuth } from "../hooks/useAuth";
import { Link } from "react-router-dom";
import Home from "@mui/icons-material/Home";
import ShoppingCart from "@mui/icons-material/ShoppingCart";
import Assessment from "@mui/icons-material/Assessment";
import Subscriptions from "@mui/icons-material/Subscriptions";
import Description from "@mui/icons-material/Description";
import Email from "@mui/icons-material/Email";
import ArrowForward from "@mui/icons-material/ArrowForward";

import {
    Card, Typography, CardContent, Box, Alert, Grid, Stack, Button
} from "@mui/material/";
import { WelcomeSchoeningh } from "./WelcomeSchoeningh";
import firebaseConfig from "../config/firebaseConfig";

export const WelcomePage = () => {
    const { claims, user } = useAuth();

    // Check if this is Schoeningh project and show specific welcome page
    if (firebaseConfig.projectId === "litport-schoeningh") {
        return <WelcomeSchoeningh />;
    }

    // Check if user is in a punchout session
    const isPunchoutSession = !!sessionStorage.getItem('punchoutSessionId');

    // Get user's display name or email
    const userName = user?.displayName || claims?.name || user?.email?.split('@')[0] || 'Benutzer';

    return (
        <BasicPage title="Willkommen bei Litport" icon={<Home />}>
            <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                {/* Welcome message */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h4" gutterBottom>
                            Willkommen, {userName}!
                        </Typography>
                        <Typography variant="h6" color="primary" gutterBottom>
                            Ihre Plattform für Literaturbestellungen
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                            Litport ist Ihr zentrales Portal für die Verwaltung und Bestellung von Literatur. 
                            Hier können Sie Zeitschriften, Bücher und andere Publikationen bestellen und Ihre Abonnements verwalten.
                        </Typography>
                    </CardContent>
                </Card>

                {/* Role-specific info */}
                {claims?.role === "Buyer" && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                        <Typography variant="body2">
                            <strong>Hinweis:</strong> Als Besteller/Bestellerin haben Sie Zugriff auf alle Bestellfunktionen. 
                            Für Status-Abfragen zu Bestellungen und Abonnements wenden Sie sich bitte an Ihre Einkaufsleitung.
                        </Typography>
                    </Alert>
                )}

                {/* Feature cards */}
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardContent sx={{ flexGrow: 1 }}>
                                <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                                    <ShoppingCart color="primary" />
                                    <Typography variant="h6">Bestellungen</Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    Durchsuchen Sie unser Sortiment und bestellen Sie Bücher, Zeitschriften und andere Publikationen. 
                                    Nutzen Sie die Warenkorb-Funktion für mehrere Artikel.
                                </Typography>
                            </CardContent>
                            <Box sx={{ p: 2, pt: 0 }}>
                                <Button 
                                    component={Link} 
                                    to="/web/searchmeta" 
                                    variant="contained" 
                                    color="primary"
                                    endIcon={<ArrowForward />}
                                    fullWidth
                                >
                                    Zur Meta-Suche
                                </Button>
                            </Box>
                        </Card>
                    </Grid>

                    {/* Hide Bestell-Service for punchout users */}
                    {!isPunchoutSession && (
                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                                        <Email color="primary" />
                                        <Typography variant="h6">Bestell-Service</Typography>
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary">
                                        Nutzen Sie unseren Bestell-Service für spezielle Anfragen wie Nachbestellungen, 
                                        Reklamationen oder Abbestellungen. Unser Team bearbeitet Ihr Anliegen zeitnah.
                                    </Typography>
                                </CardContent>
                                <Box sx={{ p: 2, pt: 0 }}>
                                    <Button 
                                        component={Link} 
                                        to="/web/orderservice" 
                                        variant="contained" 
                                        color="primary"
                                        endIcon={<ArrowForward />}
                                        fullWidth
                                    >
                                        Zum Bestell-Service
                                    </Button>
                                </Box>
                            </Card>
                        </Grid>
                    )}

                    {claims?.role !== "Buyer" && (
                        <>
                            <Grid item xs={12} md={6}>
                                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                                            <Assessment color="primary" />
                                            <Typography variant="h6">Bestell-Status</Typography>
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary">
                                            Verfolgen Sie den Status Ihrer Bestellungen in Echtzeit. 
                                            Sehen Sie Details zu Bestelldatum, Lieferstatus und Rechnungsinformationen.
                                        </Typography>
                                    </CardContent>
                                    <Box sx={{ p: 2, pt: 0 }}>
                                        <Button 
                                            component={Link} 
                                            to="/web/orderstatus" 
                                            variant="contained" 
                                            color="primary"
                                            endIcon={<ArrowForward />}
                                            fullWidth
                                        >
                                            Zum Bestell-Status
                                        </Button>
                                    </Box>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                                            <Subscriptions color="primary" />
                                            <Typography variant="h6">Abonnements</Typography>
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary">
                                            Verwalten Sie Ihre laufenden Abonnements. 
                                            Überprüfen Sie Lieferungen, Umlaufpläne und Verlängerungen Ihrer Zeitschriften-Abos.
                                        </Typography>
                                    </CardContent>
                                    <Box sx={{ p: 2, pt: 0 }}>
                                        <Button 
                                            component={Link} 
                                            to="/web/abonnements/abostatus" 
                                            variant="contained" 
                                            color="primary"
                                            endIcon={<ArrowForward />}
                                            fullWidth
                                        >
                                            Zu Abonnements
                                        </Button>
                                    </Box>
                                </Card>
                            </Grid>

                            {firebaseConfig.projectId === "litport-schoeningh" && (
                                <Grid item xs={12} md={6}>
                                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <CardContent sx={{ flexGrow: 1 }}>
                                            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                                                <Description color="primary" />
                                                <Typography variant="h6">Rechnungsarchiv</Typography>
                                            </Stack>
                                            <Typography variant="body2" color="text.secondary">
                                                Greifen Sie auf Ihre Rechnungen zu. 
                                                Laden Sie Rechnungsdokumente herunter und behalten Sie den Überblick über Ihre Ausgaben.
                                            </Typography>
                                        </CardContent>
                                        <Box sx={{ p: 2, pt: 0 }}>
                                            <Button 
                                                component={Link} 
                                                to="/web/rechnungsarchiv" 
                                                variant="contained" 
                                                color="primary"
                                                endIcon={<ArrowForward />}
                                                fullWidth
                                            >
                                                Zum Rechnungsarchiv
                                            </Button>
                                        </Box>
                                    </Card>
                                </Grid>
                            )}
                        </>
                    )}
                </Grid>

                {/* Getting started */}
                <Card sx={{ mt: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Erste Schritte
                        </Typography>
                        <Typography variant="body2" color="text.secondary" component="div">
                            <ol style={{ marginTop: 8, paddingLeft: 20 }}>
                                <li>Wählen Sie im oberen Menü die gewünschte Funktion aus</li>
                                <li>Bei Bestellungen: Suchen Sie nach Titeln und fügen Sie diese Ihrem Warenkorb hinzu</li>
                                <li>Vervollständigen Sie Ihre Bestellung mit den erforderlichen Angaben</li>
                                {claims?.role !== "Buyer" && (
                                    <li>Verfolgen Sie Ihre Bestellungen und Abonnements über die Status-Seiten</li>
                                )}
                            </ol>
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        </BasicPage>
    );
};
